import {html, css, LitElement} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {classMap} from 'lit/directives/class-map.js';
import {connect} from 'pwa-helpers';
import {AttributeType, ChoiceValueDecisionState} from '@viamedici-spc/configurator-ts';
import {msg} from '@lit/localize';
import arrowDown from '../../../icons/arrow-down';

import {setDimmLevel} from '../../slices/configurationSlice';
import store from '../../store';
import {
	selectSystemLuminousFluxMax,
	selectSystemLuminousFluxMin,
	selectAvailableDimmingLevel,
	selectCoefficients,
	selectDimmLevel,
	selectHasCoefficients,
	selectSpcConfiguration,
	selectSpcAttributes,
	selectSpcSession,
	selectDimmingLevel,
	selectFamilyConfig
} from '../../selectors';
import {getValueOfFeature, isDecided} from '../../util/values';
import {calculateCurrent, calculatePower} from '../../util/dimLevel';

/**
 * Configuration values
 * @property {number} selectedNumber - Default number on the slider used for power calculation and data display.
 * @property {number} defaultNumber - Default number on the slider used for slider thumb onload display.
 * @property {Array<Object>} numbers - Combined array of major and intermediate numbers, used internally for rendering the list.
 * @property {number} fixedHeightPerNumber - Fixed height for each number element, used for scrolling to a specific number.
 * @property {Object} title - An object containing the title in different languages.
 * @property {Function} isSatisfied - A function that returns a boolean, used for passing satisfied state to the widget from section config.
 * @property {type: String} action - An action to be dispatched when the selected number changes.
 */


/**
 * @classdesc Custom Vertical Slider creates a vertical range slider with major and intermediate numerical values, obtained from backend.
 * Interaction possible with clicking on values or dragging to a specific position.
 * @version 1.0.0
 * @path
 */
@customElement('vertical-slider')
export class VerticalSlider extends connect(store)(LitElement) {

	static styles = css`
		.verticalSlider {
			break-inside: avoid;
		}
		.arrow-box {
			background: var(--color-white);
			padding: 10px 0;
			font-size: var(--font-size-widget-small);
			color: var(--color-black);
			font-family: var(--font-family-light), sans-serif;
			position: absolute;
			right: auto;
			width: calc(100% - 25px);
			margin: 0 0 0 25px;
			z-index: 9;
			cursor: grab;
		}
		.arrow-box:before {
			background-color: var(--color-white);
			content: "";
			display: inline-block;
			height: 1px;
			left: -20px;
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			width: 20px;
		}

		.selected-number-display {
			text-align: center;
			padding: 0 8px;
			margin: 0;
		}

		.data {
			font-family: var(--font-family-regular), sans-serif;
			font-size: var(--font-size-widget-small-mono);
		}

		.data p:first-child {
			margin-top: 0;
		}

		.data span {
			font-family: var(--font-family-bold), sans-serif;
			font-size: var(--font-size-widget-small-mono);
		}

		.description {
			align-self: start;
			color: var(--color-white);
			display: flex;
			height: 100%;
			border-left: 1px solid var(--color-widget-border);
			flex-direction: column;
			font-family: var(--font-family-light), sans-serif;
			font-size: var(--font-size-widget-small-mono);
			@media (max-width: 768px) {
				margin-top: calc(2 * var(--sections-gap));
			}
		}

		.description-text {
			margin-top: auto;
		}

		.description-text p {
			margin-bottom: 0;
		}

		.dimm-level {
			height:100%;
			position: relative;
		}
		.slider-container {
			position: relative;
			padding: 0;
			height: 100%;
			box-sizing: border-box;
		}
		.icon {
			width: 11px;
			height: 11px;
			padding: 5px;
			display: inline-block;
		}

		.icon svg {
			display: block;
			fill: currentColor
		}

		.icon.satisfied {
			color: var(--color-grey5);
		}

		.intermediate-number {
			color: var(--color-widget-text);
			opacity: 0.5;
			cursor: pointer;
			height: var(--vertical-slider-number-element-height);
			padding-right: 8px;
			position: relative;
			line-height: 1;
			text-align: right;
		}

		.intermediate-number::before {
			background-color: var(--color-widget-border);
			content: "";
			display: inline-block;
			height: 1px;
			right: 0;
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			width: 5px;
		}

		.major-number {
			color: var(--color-widget-text);
			cursor: pointer;
			height: var(--vertical-slider-number-element-height);
			padding-right: var(--vertical-slider-major-numbers-left-padding);
			position: relative;
			line-height: 1;
			text-align: right;
		}

		.major-number::before {
			background-color: var(--color-widget-border);
			content: "";
			display: inline-block;
			height: 1px;
			right: 0;
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			width: var(--vertical-slider-major-numbers-left-indicator);
		}

		.notSatisfied {
			color: var(--color-warn);
		}

		.numbers {
			font-family: var(--font-family-light);
			font-size: var(--vertical-slider-number-element-height);
			list-style-type: none;
			margin: 0;
			padding-bottom: 14px;
			padding-left: 0;
			padding-top: 14px;
			height: calc(100% - 28px);
			display: flex;
			flex-direction: column;
			justify-content: space-between;
		}

		.placeholder {
			border: 1px solid var(--color-widget-border);
			height: 120px;
			width: 100%;
			padding: var(--padding) var(--padding);
			box-sizing: border-box;
			font: var(--font-widget-label);
			color: var(--color-widget-border);
		}
		.range-slider-wrapper {
			border: 1px solid var(--color-widget-border);
			align-items: baseline;
			display: grid;
			grid-template-columns: 2fr 3fr 5fr;
			gap: 0;
		}

		.satisfied {			grid-template-columns: 1fr 1fr 2fr;
			color: var(--color-text-widget-title);
		}

		.title {
			display: flex;
			justify-content: space-between;
			align-items: center;
			font: var(--font-widget-title);
			color: var(--color-widget-text);
			margin-bottom: 12px;
		}
		td {
			padding-left: var(--small-padding);
		}

		#vertical-slider {
			background-color: transparent;
			border-right: 1px solid var(--color-widget-border);
			min-height: var(--vertical-slider-min-height);
			overflow-y: auto;
			position: relative;
			width: 100%;
			height: 100%;

			-ms-overflow-style: none;
			scrollbar-width: none;
			/* Hide scrollbar for Chrome, Safari and Opera */

			&::-webkit-scrollbar {
				display: none;
			}
		}

		.row {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			padding: 8px 16px;
			border-bottom: 1px solid var(--color-widget-border);
		}
		.cell.value {
			font-family: var(--font-family-bold);
		}
		.cell.label {
			font-family: var(--font-family-light);
			text-align: right;
		}

		.arrow-down {
			position: absolute;
			color: var(--color-white);
			top: 40px;
			left: calc(50% - 8px);
			transition: opacity 0.5s;
		}
		.arrow-up {
			position: absolute;
			color: var(--color-white);
			bottom: 40px;
			left: calc(50% - 8px);
			transform: rotate(180deg);
			transition: opacity 0.5s;
		}
		.hidden {
			opacity: 0;
		}
		.debug {
			padding: 5px;
			font-size: 9px;
			display: flex;
			flex-direction: row;
			gap: 10px;
		}
		.debug > div {
			flex-basis: 33%;
		}
	`;

	@property({type: Number})
	selectedNumber = null;

	@property({type: Number})
	defaultNumber = null;

	@property({type: Object})
	title = {
		de: '[Titel]',
		en: '[Title]',
		fr: '[Title]'
	};

	@property({type: Function})
	isSatisfied = null;

	@property({type: String})
	action = '';

	@property({type: Array})
	numbers = [];

	@property({type: Object})
	coefficients = null;

	fixedHeightPerNumber = 12;

	constructor() {
		super();
		this.hasCoefficients = false;
		store.subscribe(() => this.requestUpdate());
	}

	connectedCallback() {
		super.connectedCallback();
	}

	stateChanged(state) {
		this.state = state;
		this.lang = state.status.lang;
		this.debug = state.status.debug;
		this.familyConfig = selectFamilyConfig(state);
		this.noDimming = this.familyConfig?.noDimming;

		if (this.noDimming) return;

		this.dimmingLevel = selectDimmingLevel(state);
		const availableDimmingLevel = selectAvailableDimmingLevel(state);

		// filter duplicate values
		// newNumbers = newNumbers.filter((obj, pos, arr) =>  arr.map(o => o.value).indexOf(obj.value) === pos);

		this.coefficients = selectCoefficients(state);
		this.hasCoefficients = selectHasCoefficients(state);
		//console.log('coefficients', this.coefficients, this.hasCoefficients);

		this.spcSession = selectSpcSession(state);
		this.spcConfiguration = selectSpcConfiguration(state);
		this.spcAttributes = selectSpcAttributes(state);

		if (this.shouldUpdateNumbers(availableDimmingLevel)) {
			// console.log('updating numbers', newNumbers);
			if (selectDimmLevel(state) !== null) {
				this.selectedNumber = selectDimmLevel(state);
			} else {
				if (isDecided('LE_DimmingLevel_1', this.spcAttributes)) {
					this.selectedNumber = parseInt(getValueOfFeature('LE_DimmingLevel_1', this.spcConfiguration), 10);
				}
			}
			this.numbers = availableDimmingLevel;
			this.updateSliderElements();
			this.updateSliderPosition();
		}
		if (this.hasCoefficients && (
			Number.isNaN(this.selectedNumber) ||
			!this.numbers.some((number) => number.value === this.selectedNumber))
		) {
			// set first dimminglevel (max)
			this.selectedNumber = this.numbers[0]?.value;
			this.updateSliderPosition();
			this.dispatchStatusEvents();
		}
		//console.log('stateChanged', this.coefficients, this.numbers, this.selectedNumber);
	}

	// compare current and new numbers, return true if different
	shouldUpdateNumbers(newNumbers)  {
		return JSON.stringify(this.numbers) !== JSON.stringify(newNumbers);
	}

	updated(changedProperties) {
		if (this.noDimming) return;
		if (changedProperties.has('numbers', changedProperties) ||
			changedProperties.has('coefficients', changedProperties)) {
			this.updateSliderElements();
			this.dispatchStatusEvents();
		}
	}

	firstUpdated() {
		super.firstUpdated();
		this.updateSliderElements();
	}

	updateSliderElements() {
		if (this.numbers.length === 0) return; // return if no numbers are available
		const verticalSlider = this.shadowRoot.getElementById('slider-container');
		if (!verticalSlider) return;

		const arrowBox = this.shadowRoot.querySelector('.arrow-box');
		const numbersList = this.shadowRoot.querySelector('.numbers');
		const totalHeight = numbersList?.offsetHeight - 40 ?? 300;
		const heightPerNumber = totalHeight / (this.numbers.length - 1);
		// Clean up previous event listeners if they exist
		if (this.dragCleanup) {
			this.dragCleanup();
		}

		let isDragging = false;
		let startY = 0;
		let startTop = 0;
		let sliderBounds = verticalSlider.getBoundingClientRect();

		// Function to find closest snap point
		const findClosestSnapPoint = (yPosition) => {
			const index = Math.round(yPosition / heightPerNumber);
			return Math.max(0, Math.min(this.numbers.length, index)) * heightPerNumber;
		};

		// Mouse move event
		const handleMouseMove = (e) => {
			if (!isDragging) return;

			const deltaY = e.clientY - startY;
			let newY = startTop + deltaY;

			// Constrain to slider bounds
			newY = Math.max(0, Math.min(sliderBounds.height - arrowBox.offsetHeight, newY));
			const snapY = findClosestSnapPoint(newY);

			// Update position
			this.setArrowBoxPosition(snapY);

			// Update selection during drag
			this.updateSelectedNumber(false);
		};

		// Mouse up event
		const handleMouseUp = (e) => {
			if (!isDragging) return;
			isDragging = false;

			// Get current position
			const currentY = this.getArrowPoxVerticalPositionFromDom();

			// Snap to closest point
			const snapY = findClosestSnapPoint(currentY);
			this.setArrowBoxPosition(snapY);

			// Final update with dispatch
			this.updateSelectedNumber(true);

			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		// Mouse down event
		const handleMouseDown = (e) => {
			isDragging = true;
			startY = e.clientY;
			// Get current position from transform or computed style
			startTop = this.getArrowPoxVerticalPositionFromDom();

			// Update bounds
			sliderBounds = verticalSlider.getBoundingClientRect();

			e.preventDefault();
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		};

		// Add event listeners
		arrowBox.addEventListener('mousedown', handleMouseDown);

		// Store cleanup function for next time
		this.dragCleanup = () => {
			arrowBox.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		// Initialize position
		if (this.selectedNumber) {
			this.updateSliderPosition();
		}
	}



	/**
	 * Renders the list of numbers (both major and intermediate) as HTML elements.
	 * Each number is represented as a list item with a class indicating its type.
	 *
	 * @returns {TemplateResult} A LitElement template result containing the rendered list of numbers.
	 */
	renderNumbers() {
		return this.numbers
			.map((numberObj) => {
			const className =
				numberObj.type === 'major' ? 'major-number' : 'intermediate-number';
			return html`
				<li class='${className}' @click='${(e) => this.handleNumberClick(e, numberObj.value)}'>
					${numberObj.value}
				</li>`;
		});
	}

	/**
	 * Handles click events on the numbers in the range slider.
	 * Calculates the new position for the arrow box based on the clicked number
	 * and animates the arrow box to that position.
	 *
	 * @param {Event} e - The click event object.
	 * @param {number} number - The number that was clicked.
	 */
	handleNumberClick(e, number) {
		this.selectedNumber = number;
		this.updateSliderPosition();
		this.dispatchStatusEvents();
	}

	updateSliderPosition() {
		// Using the fixed height for each number to ensure correct positioning
		const index = this.numbers.findIndex(n => n.value === this.selectedNumber);
		const numbersList = this.shadowRoot.querySelector('.numbers');
		const totalHeight = numbersList?.offsetHeight - 40 ?? 300;
		const heightPerNumber = totalHeight / (this.numbers.length - 1);
		this.setArrowBoxPosition(heightPerNumber * index);
	}

	getArrowPoxVerticalPositionFromDom() {
		const arrowBox = this.shadowRoot.querySelector('.arrow-box');
		if (!arrowBox) return 0;
		const transform = arrowBox.style.transform;
		// parse int value from "translateY(Xpx)"
		const match = transform.match(/translateY\((\d+(?:\.\d+)?)px\)/);
		if (match && match[1]) {
			return parseInt(match[1], 10);
		}
		return 0;
	}

	setArrowBoxPosition(yPosition) {
		const arrowBox = this.shadowRoot.querySelector('.arrow-box');
		if (!arrowBox) return;
		arrowBox.style.transform = `translateY(${yPosition}px)`;
	}

	/**
	 * Updates and displays the selected number based on the position of the arrow box.
	 * if dispatch is true, also dispatches an action to update the dimmLevel state.
	 * Called onDragEnd and when clicked
	 *
	 * @param {boolean} dispatch - dispatch redux events
	 * @memberof VerticalSlider
	 */
	updateSelectedNumber(dispatch = false) {
		const yPosition = this.getArrowPoxVerticalPositionFromDom();
		const numbersList = this.shadowRoot.querySelector('.numbers');
		const totalHeight = numbersList?.offsetHeight - 40 ?? 300;
		const heightPerNumber = totalHeight / (this.numbers.length - 1);
		const index = Math.round(yPosition / heightPerNumber);
		const clampedIndex = Math.max(0, Math.min(this.numbers.length - 1, index));
		if (this.selectedNumber !== this.numbers[clampedIndex].value) {
			this.selectedNumber = this.numbers[clampedIndex].value;
		}
		if (dispatch) {
			this.dispatchStatusEvents();
		}
	}

	/**
	 * dispatches actions to update the dimmLevel state and the decision
	 * dimmlevel is set on status
	 * decision will be send for the next major number in the list
	 */
	dispatchStatusEvents() {
		if (!this.selectedNumber || !this.hasCoefficients) return;
		if (this.selectedNumber !== selectDimmLevel(this.state)) {
			store.dispatch(setDimmLevel(this.selectedNumber));
		}

		// find the next major number in this.numbers that is larger then this.selectedNumber
		const nextMajorNumber = this.numbers.slice().reverse().find((number) => number.value >= this.selectedNumber && number.type === 'major');
		if (!nextMajorNumber || nextMajorNumber.value === this.dimmingLevel) return;
		this.spcSession.makeDecision({
				type: AttributeType.Choice,
				attributeId: {localId: 'LE_DimmingLevel_1'},
				choiceValueId: nextMajorNumber.value + '',
				state: ChoiceValueDecisionState.Included
			}
		);
	}

	/**
	 * Checks if the slider has a selected number.
	 *
	 * @return {*} - Returns true if the slider has a selected number, false otherwise.
	 * @memberof VerticalSlider
	 */
	isSliderSatisfied() {
		return this.selectedNumber !== null && this.selectedNumber;
	}

	debugInfo() {
		return !this.debug ? null : html`
				<div class="debug">
					<div>
						<strong>F1 (mA):</strong><br>
						${this.coefficients.formular1.coefficients.map((c, i) => (html`${c} * lum<sup>${i}</sup><br>`))}
					</div>
					<div>
						<strong>F2 (W):</strong><br>
						${this.coefficients.formular2.coefficients.map((c, i) => (html`${c} * lum<sup>${i}</sup><br>`))}
					</div>
					<div>
						<strong>${this.numbers.length} steps:</strong><br>
						${this.numbers.map((n) => n.value).join(', ')}
					</div>
				</div>
			`;
	}
	/**
	 * Renders the component.
	 *
	 * @return {*} - Returns a LitElement template result.
	 * @memberof VerticalSlider
	 */
	render() {
		if (this.noDimming) return null;

		const luminousLabel = {
			en: 'Luminous flux',
			fr: 'Flux lumineux',
			de: 'Lichtstrom',
		};
		const powerLabel = {
			en: 'Power',
			fr: 'Puissance',
			de: 'Leistung',
		};
		const currentLabel = {
			en: 'Current',
			fr: 'Actuel',
			de: 'Stromstärke',
		};
		const descriptionText = {
			en: '',
			fr: '',
			de: '',
		};
		const s = store.getState();

		if (!this.numbers || this.numbers.length === 0 || !this.hasCoefficients) {
			return html`
				<div>
					<div class="title">${this.title[this.lang]}</div>
					<div class="placeholder">${msg('Select required attributes to change dimming levels.')}</div>
				</div>
				${this.debugInfo()}
			`;
		} else {
			const index = this.numbers.findIndex(n => n.value === this.selectedNumber);
			const yPositionPercent = (index / this.numbers.length);
			return html`
				<div class="verticalSlider">
					<div class="title">${this.title[this.lang]}</div>
					<div class="range-slider-wrapper">
						<div class="dimm-level">
							<div id="vertical-slider" class="slider-wrapper">
								<ul class="numbers">
									${this.renderNumbers()}
								</ul>
							</div>
						</div>
						<div class="slider-container" id="slider-container">
							<div class="arrow-box">
								<span class="icon arrow-up ${classMap({hidden: yPositionPercent < 0.2})}">${arrowDown}</span>
								<div class="number-display">
									<p class="selected-number-display">${this.selectedNumber} <span>lm</span></p>
								</div>
								<span class="icon arrow-down ${classMap({hidden: yPositionPercent > 0.8})}">${arrowDown}</span>
							</div>
						</div>
						<div class="description">
							<div class="data">
								<div class="table">
									<div class="row">
										<div class="cell value">
											<spc-values-label attributeKey="LE_DimmingLevel_1" display="selected" context="FV.S"
															  .showIdWhenMissing=${false}></spc-values-label>
										</div>
										<div class="cell label">${luminousLabel[this.lang]}</div>
									</div>
									<div class="row">
										<div class="cell value">${this.selectedNumber} lm</div>
										<div class="cell label">${msg('dimmed')} ${luminousLabel[this.lang]}</div>
									</div>
									<div class="row">
										<div class="cell value">
											${this.hasCoefficients ? calculatePower(this.selectedNumber, this.coefficients.formular2.coefficients) : '-'}
											W
										</div>
										<div class="cell label">${powerLabel[this.lang]}</div>
									</div>
									<div class="row">
										<div class="cell value">
											${this.hasCoefficients ? calculateCurrent(this.selectedNumber, this.coefficients.formular1.coefficients) : '-'}
											mA
										</div>
										<div class="cell label">${currentLabel[this.lang]}</div>
									</div>
								</div>
							</div>
							<div class="description-text">
								<p>${descriptionText[this.lang]}</p>
							</div>
						</div>
					</div>
					${this.debugInfo()}
				</div>
			`;
		}
	}
}
